import { Link } from 'react-router-dom';
import type { Vehicle } from '@/data/siteData';
import VehiclePrice from '@/components/feature/VehiclePrice';

interface VehicleCardProps {
  vehicle: Vehicle;
  index?: number;
}

export default function VehicleCard({ vehicle, index = 0 }: VehicleCardProps) {
  return (
    <div
      className="group flex flex-col h-full bg-white rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {/* Photo — hauteur identique pour toutes les cartes */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-background-200 shrink-0">
        {vehicle.photos[0] ? (
          <img loading="lazy" decoding="async"
            src={vehicle.photos[0]}
            alt={`${vehicle.brand} ${vehicle.model} ${vehicle.motorisation}`}
            className="w-full h-full object-cover object-bottom transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-foreground-400 text-sm">
            Photo à venir
          </div>
        )}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-medium text-secondary-900">
          <i className="ri-map-pin-line w-3.5 h-3.5 flex items-center justify-center text-secondary-500" />
          {vehicle.location}
        </span>
      </div>

      {/* Contenu — flex colonne, le bouton est poussé en bas */}
      <div className="flex flex-col flex-1 p-4 md:p-5">
        {/* Titre + motorisation — hauteur réservée identique */}
        <div className="min-h-[2.75rem] md:min-h-[3rem]">
          <h3 className="font-heading font-bold text-base md:text-lg text-foreground-900 leading-snug line-clamp-1">
            {vehicle.brand} {vehicle.model}
          </h3>
          <p className="text-foreground-500 text-xs md:text-sm mt-0.5 line-clamp-1">
            {vehicle.motorisation}
          </p>
        </div>

        {/* Prix — hauteur réservée identique (promo ou non) */}
        <div className="min-h-[2.5rem]">
          <VehiclePrice price={vehicle.price} promoPrice={vehicle.promoPrice} variant="card" />
        </div>

        {/* Année / km / carburant / boîte — hauteur réservée sur 2 lignes max */}
        <div className="flex flex-wrap content-start gap-x-4 gap-y-1 mt-3 text-xs text-foreground-500 min-h-[2.25rem]">
          <span className="whitespace-nowrap">{vehicle.year}</span>
          <span className="whitespace-nowrap">{vehicle.mileage.toLocaleString('fr-FR')} km</span>
          <span className="whitespace-nowrap">{vehicle.fuel}</span>
          <span className="whitespace-nowrap">{vehicle.gearbox}</span>
        </div>

        {/* Bouton — toujours aligné en bas de la carte */}
        <div className="mt-auto pt-4">
          <Link
            to={`/vehicule/${vehicle.id}`}
            className="flex items-center justify-center w-full py-2.5 rounded-md bg-primary-500 text-white font-label font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Voir le véhicule
          </Link>
        </div>
      </div>
    </div>
  );
}